import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { observable } from "mobx";
import _ from "lodash";

import { headerHeight } from "../layout/header";
import { leftMenuWidth } from "../layout/explorer";
import { hideCatalog } from "../layout/editor";
import * as Colors from "../reusable/colors";
import { HCenter, VCenter } from "../reusable/layout";
import Element, { ElementStyle } from "./element";
import {
  ItemTypes,
  Templates,
  starterComponent,
  starterProperties,
  TemplatesBlocks
} from "../constants/elements";
import { createSceneFromTemplate } from "../user/index";
import { linkProps } from "../spreadsheet/linker";
import { flattenDB } from "../spreadsheet/store";
import * as Vibrant from "node-vibrant";
import { resizeBlocks } from "../canvas/store";
import { linkBlocks } from "../spreadsheet/store";
import Canvas from "../canvas/index";

const catalogWidth = 320;

const Catalog = styled.div`
  opacity: ${p => p.hide && 0};
  height: ${p => (p.hide ? 0 : "100%")};
  background-color: ${Colors.secondary};
  width: ${catalogWidth}px;
  position: fixed;
  border-top: 1px solid ${Colors.separatorDark};
  top: ${headerHeight}px;
  left: ${leftMenuWidth}px;
  pointer-events: ${p => p.hide && "none"};
  z-index: 30;
`;

const ElementList = styled.div``;

export const Category = styled.div`
  border: 1px solid ${Colors.separatorDark};
  border-width: 1px 0px;
  width: 100%;
  padding: 10px 12px;
  background-color: ${Colors.primary};
  text-transform: capitalize;
`;

export const Elements = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

export const Switcher = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 15px;
`;

export const Button = styled.div`
  background-color: ${p => (p.active ? Colors.separatorDark : Colors.primary)};
  box-shadow: ${p => !p.active && "1px 1px 2px " + Colors.shadow};
  cursor: ${p => (p.active ? "default" : "pointer")};
  border-radius: 2px;
  width: 45%;
  padding: 6px 5px;
  text-align: center;
`;

const PreviewContainer = styled.div`
  border: 1px solid ${Colors.separatorLight};
  border-top: 1px solid ${Colors.separatorDark};
  border-left: 1px solid ${Colors.separatorDark};
  position: fixed;
  top: ${headerHeight}px;
  left: ${catalogWidth + leftMenuWidth}px;
`;

const PreviewWidth = 400;

const ElementPreview = styled.div`
  ${HCenter};
  ${VCenter};
  background: radial-gradient(ellipse at center, #d8dee9 5%, #9fa4b5 100%);
  opacity: 0.95;
  width: ${PreviewWidth}px;
  height: 300px;
`;

const ScenePreview = styled.div`
  width: ${PreviewWidth}px;
  overflow: hidden;
  height: ${p => p.height}px;
`;

const PreviewHeader = styled.div`
  background-color: ${Colors.secondary};
  height: 59px;
  text-align: center;
  line-height: 59px;
  border-bottom: 1px solid ${Colors.separatorDark};
`;

const state = observable({
  showElements: true,
  preview: false,
  scenePreview: false
});

const handleSceneClicked = (scene, customs) => () => {
  hideCatalog();
  createSceneFromTemplate(scene, customs || {});
};

const handleSceneHover = (template, customs) => () => {
  state.scenePreview = _.mapValues(_.merge(TemplatesBlocks[template], customs), block => {
    return { ...starterProperties(block.type), ...block };
  });
};

const handleIntro = swatch => () => {
  Vibrant.from(flattenDB.get()["twitch.profile_banner"]).getPalette((err, p) => {
    handleSceneClicked(Templates.Intro, {
      canvas: { backgroundColor: "rgb(" + p[swatch].getRgb().join(",") + ")" }
    })();
  });
};

const handleIntroHover = swatch => () => {
  Vibrant.from(flattenDB.get()["twitch.profile_banner"]).getPalette((err, p) => {
    const customs = {
      canvas: { backgroundColor: "rgb(" + p[swatch].getRgb().join(",") + ")" }
    };
    handleSceneHover(Templates.Intro, customs)();
  });
};

const Intros = ["Vibrant", "Muted", "DarkMuted"];

const catalogData = {
  basics: [ItemTypes.ELEMENT_TEXT, ItemTypes.ELEMENT_IMAGE, ItemTypes.ELEMENT_EMPTYCONTAINER],
  advanced: [
    ItemTypes.ELEMENT_SLIDER,
    ItemTypes.ELEMENT_PROGRESSBAR,
    ItemTypes.ELEMENT_VIDEO,
    ItemTypes.ELEMENT_ICON
  ],
  clock: [ItemTypes.ELEMENT_CLOCK_DIGITAL, ItemTypes.ELEMENT_CLOCK_AMPM],
  countdown: [ItemTypes.ELEMENT_COUNTDOWN],
  twitch: [ItemTypes.ELEMENT_GAMEBOX, ItemTypes.ELEMENT_GAMENAME]
};

const renderPreview = (type, starterProps) => {
  const sprops = starterProps || starterProperties(type);
  const linkedProps = linkProps(sprops, flattenDB.get());
  return React.createElement(starterComponent(type), {
    ...linkedProps,
    borderWidth: type === ItemTypes.ELEMENT_EMPTYCONTAINER && !starterProps ? "2px " : "0",
    children:
      sprops.children &&
      sprops.children.map((c, idx) =>
        renderPreview(c.type, { ...c, key: idx, position: "absolute" })
      )
  });
};

const renderScene = blocks => {
  return <Canvas blocks={resizeBlocks(linkBlocks(blocks), 400, false)} editing={false} />;
};

export default observer(p => {
  return (
    <Catalog hide={p.hide}>
      {state.preview && (
        <PreviewContainer>
          <PreviewHeader>Preview</PreviewHeader>
          <ElementPreview>{renderPreview(state.preview)}</ElementPreview>
        </PreviewContainer>
      )}
      {state.scenePreview && (
        <PreviewContainer>
          <PreviewHeader>Preview</PreviewHeader>
          <ScenePreview
            height={
              PreviewWidth /
              (state.scenePreview.canvas.streamWidth / state.scenePreview.canvas.streamHeight)
            }>
            {renderScene(state.scenePreview)}
          </ScenePreview>
        </PreviewContainer>
      )}
      <Switcher>
        <Button active={state.showElements} onClick={() => (state.showElements = true)}>
          Elements
        </Button>
        <Button active={!state.showElements} onClick={() => (state.showElements = false)}>
          Scenes
        </Button>
      </Switcher>
      {state.showElements ? (
        <ElementList>
          {Object.keys(catalogData).map(categ => {
            const nbItems = catalogData[categ].length;
            return (
              <span key={categ}>
                <Category>{categ}</Category>
                <Elements>
                  {catalogData[categ].map((type, idx) => (
                    <div
                      key={type + idx}
                      style={{ width: "33%" }}
                      onMouseEnter={() => (state.preview = type)}
                      onMouseLeave={() => (state.preview = false)}>
                      <Element
                        hidden={p.hide}
                        borderBot={idx < nbItems - (nbItems % 3 === 0 ? 3 : nbItems % 3)}
                        borderRight={(idx + 1) % 3 !== 0}
                        elementType={type}
                      />
                    </div>
                  ))}
                </Elements>
              </span>
            );
          })}
          {/* <Category>Countdown</Category>
      <Elements>
        <Element border>Simple</Element>
        <Element border>With Progress</Element>
        <Element border>Starting in</Element>
        <Element>Circle</Element>
      </Elements>
      <Category>Twitch Channel</Category>
      <Elements>
        <Element border>Game Name</Element>
        <Element border>Game Picture</Element>
        <Element border>Viewers</Element>
        <Element>Title</Element>
      </Elements>
      <Category>Poll</Category>
      <Elements>
        <Element>Strawpoll</Element>
        <Element>Strawpoll Progress</Element>
        <Element>Strawpoll Progress Inside</Element>
      </Elements> */}
        </ElementList>
      ) : (
        <span>
          <Category>Intro / Idle</Category>
          <Elements>
            {Intros.map(swatch => {
              return (
                <ElementStyle
                  key={swatch}
                  scene
                  onClick={handleIntro(swatch)}
                  onMouseEnter={handleIntroHover(swatch)}
                  onMouseLeave={() => (state.scenePreview = false)}>
                  Banner {swatch}
                </ElementStyle>
              );
            })}
            <ElementStyle
              scene
              onClick={handleSceneClicked(Templates.Idle)}
              onMouseEnter={handleSceneHover(Templates.Idle)}
              onMouseLeave={() => (state.scenePreview = false)}>
              Slider Blood
            </ElementStyle>
            <ElementStyle
              scene
              onClick={handleSceneClicked(Templates.TwitterIdle)}
              onMouseEnter={handleSceneHover(Templates.TwitterIdle)}
              onMouseLeave={() => (state.scenePreview = false)}>
              Twitter Blues
            </ElementStyle>
          </Elements>
        </span>
      )}
    </Catalog>
  );
});



// WEBPACK FOOTER //
// ./src/catalog/catalog.js